import React, { useEffect, useMemo } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { L3_PATHS } from 'Deer/constants/routes';
import { setL2Visible, useSelectIsL2Visible } from 'Deer/slice/DeerNav.slice';
import { format } from 'date-fns';
import { useDispatch } from 'react-redux';
import {
  REVIEW_FORM_KEYS,
  setCurrentFormKey,
  setCurrentFormKeyWithDataReset,
  setIsSelectedFAQSection,
  useIsValidateForm,
  useSelectedReviewForm,
  useSelectedReviewPeriodSummarySection,
} from 'reducers/reviewFormSlice';
import { Loader } from 'screens/admin/HelpCenter/helperComponents';
import { REVIEW_TABLE_TYPES } from 'screens/performanceReview/LandingPage/ReviewAudit';
import { SideNavContainer } from 'screens/performanceReview/ResultPage/components/sidebar/ReviewResultSidebar';
import { useReviewPreviewParams } from 'screens/performanceReview/hooks/reviewPreviewHooks';
import useReviewFormParams from 'screens/performanceReview/hooks/useReviewFormParams';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { MyTeam, OpenInNew, QuickHelp } from 'assets/assetsV1/iconComponents';

import { isMobileView } from 'constants/isMobileView';
import {
  sendChangedFormSectionEvent,
  sendClickedPastReviewsEvent,
  sendClickedReviewsFAQEvent,
} from 'constants/mixpanelHelpers/reviewsEvents';
import { isNetchexLogin } from 'constants/netChexConstants';
import { routes } from 'constants/routes';

import { Avatar } from 'uiToolkitV1/Avatar';
import { FormTabToggle } from 'uiToolkitV1/FormTabToggle';
import { ListItemLeftNav } from 'uiToolkitV1/ListItemLeftNav';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { OverallFormScoreText } from '../newFormComponents/OverallFormScore';
import SidebarReviewPeriodSummary from './SidebarReviewPeriodSummary';
import DividerForPanel from './common/DividerForPanel';
import REVIEW_FORM_TYPES from './common/REVIEW_FORM_TYPES';
import { useReviewFAQData, useReviewFormSidebarDataOld } from './hooks/dataHooks';

const FORM_NAMES_ORDER = [REVIEW_FORM_KEYS.GOAL, REVIEW_FORM_KEYS.COMPETENCY, REVIEW_FORM_KEYS.QNA];

const EmpReviewInfoHeader = ({ employee, reviewInfo, startDateTS, endDateTS }) => (
  <Box pt={2}>
    <TextLabel bold px="0" py={1} mb={2} TextLabel color="text.g2">
      REVIEW PROCESS FOR
    </TextLabel>
    <Flex mb={1}>
      <Avatar src={employee?.profilePhotoSrc} size={24} />
      <Box ml={2}>
        <TextBodyMedium bold>{employee?.displayName}</TextBodyMedium>
        <TextLabel color="text.g2">{employee?.designation}</TextLabel>
        <TextLabel color="text.g2">{reviewInfo?.feedbackForm?.reviewCycle?.name}</TextLabel>
        <TextLabel color="text.g2">
          {startDateTS && format(startDateTS, 'MMM d')} to {endDateTS && format(endDateTS, 'MMM d')}
        </TextLabel>
      </Box>
    </Flex>
  </Box>
);

const ReviewFormSidebar = () => {
  const { employeeId, providerId, reviewId } = useReviewFormParams();
  const dispatch = useDispatch();
  let selectedReviewForm = useSelectedReviewForm();
  const isValidateForm = useIsValidateForm();
  const { data: reviewFAQData } = useReviewFAQData({ reviewId }) ?? [];

  const selectedReviewPeriodSummarySection = useSelectedReviewPeriodSummarySection();
  if (selectedReviewPeriodSummarySection) {
    selectedReviewForm = null;
  }

  const { data, isLoading } = useReviewFormSidebarDataOld({
    reviewId,
    employeeId,
    providerId,
  });

  const isSelfReview = data?.review?.providerType === 'SELF';

  const {
    employee,
    reviewInfo,
    formNames,
    startDate: startDateTS,
    endDate: endDateTS,
    isShowGoalRating,
    isShowCompetencyRating,
    isShowQNARating,
  } = data ?? {};
  const { isPreview } = useReviewPreviewParams();

  const isL2Visible = useSelectIsL2Visible();

  const isMobileViewBool = isMobileView();

  const closeSidepanelOnClickMobile = () => {
    if (isMobileViewBool && isL2Visible) {
      dispatch(
        setL2Visible({
          isL2Visible: false,
        })
      );
    }
  };

  const setReviewForm = ({ id }) => {
    dispatch(setCurrentFormKey({ id }));
  };

  // run once on first render to close side panel on mobile
  useEffect(() => {
    closeSidepanelOnClickMobile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isLoading) {
      const { formName } = Array.isArray(formNames) ? (formNames[0] ?? {}) : {};
      const { id } = REVIEW_FORM_TYPES[formName] ?? {};
      dispatch(setCurrentFormKeyWithDataReset({ id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, dispatch]);

  const formTabToggleOptions = useMemo(
    () =>
      formNames
        ?.map((form) => {
          const { formName, unansweredRequiredQuestions } = form;
          const { id, title } = REVIEW_FORM_TYPES[formName];

          let showScore = false;
          if (isShowGoalRating && formName === REVIEW_FORM_KEYS.GOAL) {
            showScore = true;
          } else if (isShowCompetencyRating && formName === REVIEW_FORM_KEYS.COMPETENCY) {
            showScore = true;
          } else if (isShowQNARating && formName === REVIEW_FORM_KEYS.QNA) {
            showScore = true;
          }

          return {
            id,
            title, // Use updatedTitle here
            subTitle:
              unansweredRequiredQuestions === 0 && showScore ? (
                <OverallFormScoreText reviewFormKey={formName} isTextOnly />
              ) : (
                <TextLabel
                  color={isValidateForm && unansweredRequiredQuestions !== 0 ? '#C62C02 !important' : 'text.g2'}
                >
                  {unansweredRequiredQuestions > 1
                    ? `${unansweredRequiredQuestions} questions left`
                    : `${unansweredRequiredQuestions} question left`}
                </TextLabel>
              ),
            isComplete: unansweredRequiredQuestions === 0,
          };
        })
        .sort((a, b) => FORM_NAMES_ORDER.indexOf(a.id) - FORM_NAMES_ORDER.indexOf(b.id)),
    [formNames, isShowCompetencyRating, isShowGoalRating, isShowQNARating, isValidateForm]
  );

  if (isLoading) {
    return (
      <Box p={4}>
        <Loader />
      </Box>
    );
  }

  return (
    <SideNavContainer>
      <EmpReviewInfoHeader
        employee={employee}
        reviewInfo={reviewInfo}
        startDateTS={startDateTS}
        endDateTS={endDateTS}
      />
      <DividerForPanel />

      <FormTabToggle
        options={formTabToggleOptions}
        selectedOptionId={selectedReviewForm}
        flexGrow={1}
        changeHandler={(id) => {
          sendChangedFormSectionEvent({
            eventProperties: {
              empId: employeeId,
              sectionName: id,
              source: 'side navigation',
            },
          });
          closeSidepanelOnClickMobile();
          setReviewForm({ id });
        }}
      />

      {!isMobileView() && !isPreview && (
        <SidebarReviewPeriodSummary employeeId={employeeId} reviewId={reviewId} providerId={providerId} />
      )}

      {!isMobileView() && isSelfReview && !isPreview && (
        <>
          <DividerForPanel />
          <ListItemLeftNav
            LeftIconComponent={MyTeam}
            mb={1}
            onClick={() => {
              sendClickedPastReviewsEvent({
                eventProperties: {
                  reviewCycleId: reviewId,
                  empId: employeeId,
                },
              });
              const url = `${
                checkNewNavEnabled() ? L3_PATHS['my-data'].review.myReviews : routes.performanceReview.audit
              }?activeTableId=${REVIEW_TABLE_TYPES.MY_PAST_REVIEWS}`;
              window.open(url, '_blank');
            }}
            title="View Past Reviews"
          />
        </>
      )}

      {!isMobileView() && // Check if isMobileView is false
        !isPreview &&
        !isNetchexLogin && (
          <>
            <DividerForPanel />
            {reviewFAQData && reviewFAQData[0]?.url ? (
              <a href={reviewFAQData[0]?.url} target="_blank" download rel="noreferrer">
                <ListItemLeftNav
                  LeftIconComponent={MyTeam}
                  RightComponent={OpenInNew}
                  mb={1}
                  onClick={() => {
                    // todo: vikalp
                  }}
                  title="Reviews FAQ"
                />
              </a>
            ) : (
              <ListItemLeftNav
                LeftIconComponent={MyTeam}
                RightComponent={OpenInNew}
                mb={1}
                onClick={() => {
                  dispatch(setIsSelectedFAQSection(true));
                  sendClickedReviewsFAQEvent({
                    eventProperties: {
                      reviewCycleId: reviewId,
                      empId: employeeId,
                    },
                  });
                }}
                title="Reviews FAQ"
              />
            )}
            {!checkNewNavEnabled() && (
              <ListItemLeftNav LeftIconComponent={QuickHelp} mb={1} onClick={() => {}} title="Quick Help" />
            )}
          </>
        )}
    </SideNavContainer>
  );
};

export default ReviewFormSidebar;
